import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import HomeCard from '../components/HomeCard';
import { getHomesByCategory, getCategoryDisplayName } from '../lib/homes';
import { HomeCategory } from '../types';

const CategoryPage: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const categoryKey = category as HomeCategory;
  const homes = getHomesByCategory(categoryKey);
  const categoryName = getCategoryDisplayName(categoryKey);

  const categoryDescriptions = {
    core: {
      description: "Our Core Homes collection offers essential living spaces designed for modern lifestyles. These homes provide practical, well-designed layouts that deliver excellent value without compromising on quality or style.",
      features: [
        "Efficient use of space with thoughtful design",
        "Quality finishes and modern amenities",
        "Flexible layouts that adapt to your needs",
        "Excellent value for money",
        "Low maintenance materials and design"
      ]
    },
    family: {
      description: "Family Homes are designed with growing families in mind. These spacious designs feature multiple living areas, generous bedrooms, and thoughtful layouts that provide room for everyone to live, work, and play.",
      features: [
        "Multiple living areas for family activities",
        "Spacious bedrooms and storage solutions",
        "Open-plan designs for family interaction",
        "Separate zones for adults and children",
        "Outdoor entertaining areas"
      ]
    },
    kiwiana: {
      description: "Our Kiwiana Homes celebrate New Zealand's unique architectural heritage. These designs combine traditional Kiwi elements with modern comfort, creating homes that are both nostalgic and contemporary.",
      features: [
        "Traditional New Zealand design elements",
        "Natural materials and sustainable features",
        "Indoor-outdoor living integration",
        "Heritage-inspired details and finishes",
        "Connection to New Zealand's natural environment"
      ]
    }
  };

  const otherCategories = [
    { key: 'core', name: 'Core Homes' },
    { key: 'family', name: 'Family Homes' },
    { key: 'kiwiana', name: 'Kiwiana Homes' }
  ].filter(cat => cat.key !== categoryKey);

  if (!categoryKey || !categoryDescriptions[categoryKey]) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#1F2937] mb-4">Category Not Found</h1>
          <p className="text-[#334155] mb-8">The category you're looking for doesn't exist.</p>
          <Link
            to="/homes"
            className="bg-[#FF5A1F] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#e4501c] transition-colors duration-200"
          >
            View All Homes
          </Link>
        </div>
      </div>
    );
  }

  const categoryInfo = categoryDescriptions[categoryKey];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Helmet>
        <title>{categoryName} - Rolleston Homes | Quality New Zealand Homes</title>
        <meta name="description" content={categoryInfo.description} />
      </Helmet>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-[#334155] hover:text-[#FF5A1F] transition-colors duration-200">
              Home
            </Link>
            <span className="text-gray-400">/</span>
            <Link to="/homes" className="text-[#334155] hover:text-[#FF5A1F] transition-colors duration-200">
              All Homes
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-[#FF5A1F] font-medium">{categoryName}</span>
          </nav>
        </div>
      </div>

      {/* Page Header */}
      <section className="bg-white py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="flex items-center mb-6">
            <Link
              to="/homes"
              className="flex items-center space-x-2 text-[#FF5A1F] hover:text-[#e4501c] transition-colors duration-200 mr-6"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to All Homes</span>
            </Link>
          </div>
          
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-extrabold text-[#1F2937] mb-6">
              {categoryName}
            </h1>
            <p className="text-lg text-[#334155] max-w-3xl mx-auto mb-8">
              {categoryInfo.description}
            </p>
            
            {/* Category Features */}
            <div className="bg-[#FAFAFA] rounded-2xl p-8 max-w-4xl mx-auto">
              <h3 className="text-xl font-bold text-[#1F2937] mb-6">Key Features</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {categoryInfo.features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#FF5A1F] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-[#334155]">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Homes Grid */}
      <section className="py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-3xl font-bold text-[#1F2937]">
              {homes.length} {categoryName} Available
            </h2>
            <Link
              to="/homes"
              className="text-[#FF5A1F] hover:text-[#e4501c] font-semibold transition-colors duration-200"
            >
              View All Categories
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {homes.map((home) => (
              <HomeCard
                key={home.id}
                home={home}
                showCategory={false}
                featured={home.isFeatured}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Explore Other Categories */}
      <section className="bg-white py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <h2 className="text-3xl font-bold text-[#1F2937] text-center mb-12">
            Explore Other Categories
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {otherCategories.map((cat) => (
              <Link
                key={cat.key}
                to={`/homes/${cat.key}`}
                className="group bg-[#FAFAFA] rounded-2xl p-8 hover:shadow-lg transition-all duration-200 transform hover:-translate-y-1"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-[#1F2937] mb-2 group-hover:text-[#FF5A1F] transition-colors duration-200">
                      {cat.name}
                    </h3>
                    <p className="text-[#334155] text-sm mb-4">
                      {categoryDescriptions[cat.key as HomeCategory].description.substring(0, 120)}...
                    </p>
                    <div className="flex items-center space-x-2 text-[#FF5A1F] font-semibold">
                      <span>Explore {cat.name}</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CategoryPage;